<script lang="ts" setup>
import { IYearMonth } from '@/coreDomainsModule/date';
import {
  ForecastForReceiptHeaderKey,
  isCustomerForecastWithDiffLastMonthKey,
  TableContentProperty,
} from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import TableCell from '../TableCell.vue';
import SupplierForecastCreationStatusIcon from './SupplierForecastCreationStatusIcon.vue';
import { ForecastForReceiptTableContentViewModel } from './forecastForReceiptTableContentViewModel';
import { computed } from 'vue';
import ForecastForReceiptTableCell from './ForecastForReceiptTableCell.vue';
import ForecastDiffLastMonthTableCell from './ForecastDiffLastMonthTableCell.vue';

// props
const props = withDefaults(
  defineProps<{
    header: TableContentProperty;
    headerKey: ForecastForReceiptHeaderKey;
    item: TableContentItem;
    headerIndex: number;
    accumulationTableContentWidth: number;
    isVariableWidth: boolean;
    isAllSelected: boolean;
    stableColumnCount: number;
    isBold: boolean;
    forecastStartYearMonth?: IYearMonth;
    isChecked: boolean;
    isCheckedBusinessId: boolean;
  }>(),
  {
    forecastStartYearMonth: undefined,
  }
);

// emits
const emits = defineEmits<{
  (e: 'onClickNarrow'): void;
  (e: 'onClickTransitionTimeline'): void;
  (e: 'onClickTransactionList'): void;
  (e: 'onClickDownload'): void;
  (e: 'onClickForecastRequestMail'): void;
  (e: 'onClickEdit'): void;
  (e: 'onClickRemoveFromForecast'): void;
}>();

const forecastForReceiptTableContentVM = new ForecastForReceiptTableContentViewModel();

const textAlign = computed(() => {
  if (props.header.key === 'SupplierForecastCreateStatus') {
    return 'center';
  }
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});
</script>

<template>
  <ForecastDiffLastMonthTableCell
    v-if="isCustomerForecastWithDiffLastMonthKey(header.key)"
    :header="header"
    :header-key="headerKey"
    :item="item"
    :header-index="headerIndex"
    :accumulation-table-content-width="accumulationTableContentWidth"
    :is-variable-width="isVariableWidth"
    :is-all-selected="isAllSelected"
    :stable-column-count="stableColumnCount"
    :is-bold="isBold"
    :forecast-start-year-month="forecastStartYearMonth"
    :is-checked="props.isChecked"
    :is-checked-business-id="props.isCheckedBusinessId"
    @on-click-narrow="emits('onClickNarrow')"
  />
  <ForecastForReceiptTableCell
    v-else
    :header="header"
    :header-key="headerKey"
    :item="item"
    :header-index="headerIndex"
    :accumulation-table-content-width="accumulationTableContentWidth"
    :is-variable-width="isVariableWidth"
    :is-all-selected="isAllSelected"
    :stable-column-count="stableColumnCount"
    :is-bold="isBold"
    :forecast-start-year-month="forecastStartYearMonth"
    :is-checked="props.isChecked"
    :is-checked-business-id="props.isCheckedBusinessId"
    @on-click-narrow="emits('onClickNarrow')"
    @on-click-transition-timeline="emits('onClickTransitionTimeline')"
    @on-click-transaction-list="emits('onClickTransactionList')"
    @on-click-download="emits('onClickDownload')"
    @on-click-forecast-request-mail="emits('onClickForecastRequestMail')"
    @on-click-edit="emits('onClickEdit')"
    @on-click-remove-from-forecast="emits('onClickRemoveFromForecast')"
  />
</template>

<style scoped lang="scss">
.table-cell-content {
  font-family: Noto Sans JP;
  font-size: $fontSizeSS;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &--bold {
    font-weight: bold;
  }
}
</style>
